// sidebarEntries — what modules register into the left menu, and the pure
// rules that turn registrations into what it shows (pen "Left menu – detail
// design", decided 2026-10-20).
//
// The menu is shell chrome: it knows no views. Modules register entries; the
// shell's composition root collects them and passes the list in. One small
// registry, three kinds (the spec's open question 1, answered as one list:
// one place to register, one fold to split it):
//   - menu-entry  — a main view (name, icon, "works yet");
//   - recent-item — something recently worked on (its list, title, what it
//     can do);
//   - foot-row    — a row in the foot (Give feedback, Events, …).
// Rules: an entry that does not work yet is hidden, never disabled; an action
// a recent item's kind can't do is absent, so its menu hides it; how recent
// items are pinned, sorted and capped is recentList.ts; an entry with no icon
// the menu knows gets the generic dot.
//
// Pure: data and functions only.

export type { RecentItem, SchoolSwitcher, SidebarEntry };
export { countLabel, iconOf, markOf, schoolSwitcherOf, sidebarSectionsOf };

import { exhaustSwitchCase } from "@bilda/utils";
import type { IconName } from "../Icon/Icon.js";

type MenuEntry = {
    readonly type: "menu-entry";
    readonly id: string;
    readonly label: string;
    // Absent: the menu draws its generic dot.
    readonly icon?: IconName;
    readonly works: boolean;
    readonly current: boolean;
    readonly open: () => void;
};

type RecentItem = {
    readonly type: "recent-item";
    readonly id: string;
    // The list it is registered into (a `RecentListProps` id, e.g.
    // "projects"); an item whose list the menu doesn't show is not shown.
    readonly list: string;
    readonly title: string;
    // When it last changed (ISO 8601), for "Last changed".
    readonly changedAt?: string;
    // The Detailed view's second line ("Year 7 · 2 modules · today").
    readonly meta?: string;
    readonly current: boolean;
    readonly open: () => void;
    // What its kind can do; an absent action is not in its "…" menu (pin and
    // "Remove from Recent" are the menu's own, for every kind).
    readonly rename?: () => void;
    readonly duplicate?: () => void;
    readonly archive?: () => void;
    // Present on an archived item: its menu is Restore and Delete … only.
    readonly restore?: () => void;
    readonly confirmDelete?: () => void;
    // Present while the item is being renamed in place.
    readonly renaming?: {
        readonly onSubmit: (title: string) => void;
        readonly onCancel: () => void;
    };
};

type FootRow = {
    readonly type: "foot-row";
    readonly id: string;
    readonly label: string;
    readonly icon?: IconName;
    readonly works: boolean;
    // Unread count; 0 shows nothing.
    readonly count: number;
    readonly open: () => void;
};

type SidebarEntry = MenuEntry | RecentItem | FootRow;

type SidebarSections = {
    readonly menu: readonly MenuEntry[];
    readonly recent: readonly RecentItem[];
    readonly foot: readonly FootRow[];
};

const empty: SidebarSections = { menu: [], recent: [], foot: [] };

// Split the registrations into the menu's sections, in registration order.
const sidebarSectionsOf = (entries: readonly SidebarEntry[]): SidebarSections =>
    entries.reduce<SidebarSections>((acc, entry) => {
        switch (entry.type) {
            case "menu-entry":
                return entry.works
                    ? { ...acc, menu: [...acc.menu, entry] }
                    : acc;
            case "recent-item":
                return { ...acc, recent: [...acc.recent, entry] };
            case "foot-row":
                return entry.works
                    ? { ...acc, foot: [...acc.foot, entry] }
                    : acc;
            default:
                return exhaustSwitchCase(entry);
        }
    }, empty);

const iconOf = (icon: IconName | undefined): IconName => icon ?? "dot";

// "3", or "9+" past nine; "" for none.
const countLabel = (count: number): string =>
    count <= 0 ? "" : count > 9 ? "9+" : String(count);

// === School switcher =======================================================

type Institution = {
    readonly id: string;
    readonly name: string;
};

// Two or more institutions, with switching wired: a switcher (chevron and
// menu). Anything else shows nothing (pen Q82: the name only for people in
// more than one).
type SchoolSwitcher =
    | { readonly type: "none" }
    | {
          readonly type: "switcher";
          readonly current: Institution;
          readonly all: readonly Institution[];
          readonly onSwitch: (id: string) => void;
      };

const schoolSwitcherOf = (
    institutions: readonly Institution[],
    currentId: string,
    onSwitch: ((id: string) => void) | undefined,
): SchoolSwitcher => {
    const current =
        institutions.find((i) => i.id === currentId) ?? institutions[0];
    return current !== undefined &&
        institutions.length >= 2 &&
        onSwitch !== undefined
        ? { type: "switcher", current, all: institutions, onSwitch }
        : { type: "none" };
};

// The mark's letter: the name's first letter (any script), upper-cased.
const markOf = (name: string): string =>
    (name.match(/\p{L}|\p{N}/u)?.[0] ?? "?").toLocaleUpperCase();
